// Quittances (H4-08) : une par paiement confirmé ou reçu de caisse vérifié, numéro, montant,
// rubrique, code de vérification public, PDF produit par la tâche de fond, état (valide ou annulée
// avec motif) ; export CSV journalisé.
import { Bandeau, Bouton, EtatVide, Filtres, Lien, PuceEtat, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { Download, FileCheck2 } from 'lucide-react';
import { useState } from 'react';
import { messageErreur } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { dateHeure } from '../../commun/format';
import { LIBELLES_ETAT_QUITTANCE, francs, libelle, type QuittanceReponse } from './donnees';
import { NavigationFinances, useDroitsRecettes } from './NavigationFinances';
import { BoutonPdfQuittance } from './PageCreance';
import { telechargerFichier, useQuittances } from './requetes';
import styles from './recettes.module.css';

export function PageQuittances() {
  const droits = useDroitsRecettes();
  const [filtres, setFiltres] = useFiltresPersistes('recettes-quittances', { etat: '' });
  const f = { etat: filtres.etat ?? '' };
  const liste = useQuittances(f);
  const [erreurExport, setErreurExport] = useState<string | null>(null);

  const colonnes: ColonneTableau<QuittanceReponse>[] = [
    { id: 'numero', entete: 'Numéro', enTeteLigne: true, masquable: false, valeurTri: (q) => q.numero, cellule: (q) => <span className={styles.mono}>{q.numero}</span> },
    { id: 'paiement', entete: 'Paiement', valeurTri: (q) => q.reference_paiement, cellule: (q) => <Lien href={`/recettes/paiements/${q.paiement_id}`} className={styles.mono}>{q.reference_paiement}</Lien> },
    { id: 'montant', entete: 'Montant', numerique: true, valeurTri: (q) => q.montant, cellule: (q) => <span className={styles.montant}>{francs(q.montant)}</span> },
    { id: 'rubrique', entete: 'Rubrique', valeurTri: (q) => q.rubrique, cellule: (q) => q.rubrique },
    { id: 'emise', entete: 'Émise le', valeurTri: (q) => q.emise_le, cellule: (q) => dateHeure(q.emise_le) },
    { id: 'code', entete: 'Code de vérification', cellule: (q) => <span className={styles.codeVerification}>{q.code_verification}</span> },
    { id: 'etat', entete: 'État', valeurTri: (q) => q.etat, cellule: (q) => (
      <>
        <PuceEtat ton={q.etat === 'valide' ? 'ok' : 'neutre'} libelle={libelle(LIBELLES_ETAT_QUITTANCE, q.etat)} />
        {q.motif_annulation ? <div className={styles.sous}>{q.motif_annulation}</div> : null}
      </>
    ) },
    { id: 'pdf', entete: 'PDF', masquable: false, cellule: (q) => <BoutonPdfQuittance q={q} /> },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Finances"
        titre="Quittances"
        description="Une quittance par paiement confirmé, vérifiable par quiconque avec son code (portail citoyen, « Vérifier un document ») sans donnée personnelle. Hors production, le PDF porte la mention de son environnement."
        actions={
          droits.exporter ? (
            <Bouton variante="secondaire" icone={Download} onClick={() => { setErreurExport(null); telechargerFichier('/recettes/quittances/export', 'quittances.csv', f).catch((e: unknown) => setErreurExport(messageErreur(e))); }}>
              Exporter en CSV
            </Bouton>
          ) : null
        }
      />
      <NavigationFinances />
      {erreurExport ? <Bandeau type="erreur" onFermer={() => setErreurExport(null)}>Export impossible : {erreurExport}</Bandeau> : null}
      <Filtres libelle="Filtres des quittances" groupes={[{ id: 'etat', libelle: 'État', options: [{ valeur: '', libelle: 'Toutes' }, { valeur: 'valide', libelle: 'Valides' }, { valeur: 'annulee', libelle: 'Annulées' }] }]} valeurs={filtres} onChanger={setFiltres} />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L2-A" fonction="La liste des quittances">
        {(liste.data ?? []).length === 0 ? (
          <EtatVide titre="Aucune quittance" icone={FileCheck2}>
            <p>Aucune quittance ne correspond à ce filtre.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Quittances" colonnes={colonnes} lignes={liste.data ?? []} cleLigne={(q) => q.id} cleColonnes="recettes-quittances" />
        )}
      </EtatRequete>
    </>
  );
}
